import { LocalDateFieldModel, RadioFieldModel, StringFieldModel } from "@/common/commonForms";
import { Language } from "@/i18n/languages";
import { TMessage } from "@/i18n/tt";
import { getAllCountriesSortedByName, getCountryName } from "@/utils/countries";
import { toLocalDateISOString } from "@/utils/Dates";

export type AddressType = "BUSINESS" | "POSTAL";

export type CompanyAddress = {
  addressLine1?: string | null;
  addressLine2?: string | null;
  postalCode?: string | null;
  postalPlace?: string | null;
  country?: string | null;
};

export type CountryOption = { code: string; name: string };

export type AddressForm = {
  addressType: AddressType;
  addressLine1: StringFieldModel;
  addressLine2: StringFieldModel;
  postalCode: StringFieldModel;
  postalPlace: StringFieldModel;
  country: RadioFieldModel<CountryOption>;
  effectiveFrom: LocalDateFieldModel;
};

export type UpdateAddressRequest = {
  addressType: AddressType;
  data: {
    addressLine1: string;
    addressLine2: string;
    postalCode: string;
    postalPlace: string;
    country: string;
    effectiveFrom?: string;
  };
};

export const addressLabels: Record<AddressType, TMessage> = {
  BUSINESS: { key: "entity-data.system-business-address" },
  POSTAL: { key: "entity-data.system-postal-address" },
};

export const addressFormTitles: Record<AddressType, TMessage> = {
  BUSINESS: { key: "settings.updateBusinessAddress.title" },
  POSTAL: { key: "settings.updatePostalAddress.title" },
};

export const addressUpdatedMessage = (addressType: AddressType): TMessage => ({
  key: "settings.update-success",
  params: { setting: addressLabels[addressType] },
});

const countryOption = (code: string | null | undefined, locale: Language): CountryOption => ({
  code: code ?? "",
  name: getCountryName(code ?? "", locale) ?? "",
});

export const newAddressForm = (
  addressType: AddressType,
  address: CompanyAddress,
  locale: Language,
): AddressForm => ({
  addressType,
  addressLine1: new StringFieldModel({ required: true, value: address.addressLine1 ?? "" }),
  addressLine2: new StringFieldModel({ value: address.addressLine2 ?? "" }),
  postalCode: new StringFieldModel({ value: address.postalCode ?? "" }),
  postalPlace: new StringFieldModel({ required: true, value: address.postalPlace ?? "" }),
  country: new RadioFieldModel<CountryOption>({
    value: countryOption(address.country, locale),
    options: getAllCountriesSortedByName(locale),
    idFn: (option) => option.code,
    labelFn: (option) => ({ translated: option.name }),
  }),
  effectiveFrom: new LocalDateFieldModel({ maxDate: toLocalDateISOString(Date.now()) }),
});

const addressTextFields = (form: AddressForm): StringFieldModel[] => [
  form.addressLine1,
  form.addressLine2,
  form.postalCode,
  form.postalPlace,
];

export const canSaveAddress = (form: AddressForm): boolean =>
  !form.addressLine1.isEmpty &&
  !form.postalPlace.isEmpty &&
  form.country.value.code !== "" &&
  addressTextFields(form).every((field) => field.isValid) &&
  !form.effectiveFrom.error;

export const toUpdateAddressRequest = (form: AddressForm): UpdateAddressRequest => ({
  addressType: form.addressType,
  data: {
    addressLine1: form.addressLine1.validatedValue,
    addressLine2: form.addressLine2.validatedValue,
    postalCode: form.postalCode.validatedValue,
    postalPlace: form.postalPlace.validatedValue,
    country: form.country.value.code,
    effectiveFrom: form.effectiveFrom.validatedValue,
  },
});
